import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { legalDocuments, type LegalTab } from '@/lib/auth-legal';

type TabType = LegalTab | 'cookie';

const tabs: { id: TabType; label: string; icon: string }[] = [
  { id: 'privacy', label: '隱私政策', icon: 'ri-shield-user-line' },
  { id: 'refund', label: '退款政策', icon: 'ri-refund-2-line' },
  { id: 'terms', label: '服務條款', icon: 'ri-file-text-line' },
  { id: 'cookie', label: 'Cookie 設定', icon: 'ri-settings-3-line' }
];

const LegalPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'privacy';
  const [activeTab, setActiveTab] = useState<TabType>(
    ['privacy', 'refund', 'terms', 'cookie'].includes(initialTab) ? initialTab : 'privacy'
  );

  const activeDocument = useMemo(
    () => legalDocuments.find((document) => document.key === activeTab),
    [activeTab]
  );

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <div className="bg-[#0A1628] py-10">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-2">
              <Link to="/" className="text-slate-500 hover:text-slate-300 text-xs cursor-pointer transition-colors">首頁</Link>
              <i className="ri-arrow-right-s-line text-slate-600 text-xs" />
              <span className="text-slate-400 text-xs">法律文件</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">法律與隱私</h1>
            <p className="text-slate-400 text-sm">最後更新：2026 年 1 月 20 日</p>
          </div>
        </div>

        <div className="bg-white border-b border-slate-100 sticky top-16 z-40">
          <div className="max-w-4xl mx-auto px-6 flex items-center gap-0.5 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold cursor-pointer transition-all whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? 'text-violet-600 border-violet-500'
                    : 'text-slate-500 hover:text-slate-800 border-transparent'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`} />
                </div>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="bg-white rounded-2xl border border-slate-100 p-8 sm:p-10 max-w-none">
            {activeDocument && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-bold text-[#0A1628] mb-3">{activeDocument.title}</h2>
                  <p className="text-slate-500 text-sm leading-relaxed">{activeDocument.subtitle}</p>
                </div>
                {activeDocument.sections.map((section) => (
                  <div key={section.title} className="rounded-2xl border border-slate-100 bg-slate-50/80 p-6">
                    <h3 className="text-sm font-bold text-[#0A1628] mb-3">{section.title}</h3>
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="text-slate-500 text-sm leading-relaxed mb-3 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                    {section.bullets && (
                      <div className="space-y-2.5">
                        {section.bullets.map((bullet) => (
                          <div key={bullet} className="flex items-start gap-3 text-slate-500 text-sm leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2 shrink-0" />
                            <span>{bullet}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'cookie' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-bold text-[#0A1628] mb-3">Cookie 設定</h2>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    我們使用 Cookie 和類似技術來改善你的瀏覽體驗。以下說明我們使用的 Cookie 類型。
                  </p>
                </div>
                {[
                  {
                    title: '必要 Cookie（始終開啟）',
                    badge: '必要',
                    badgeColor: 'bg-emerald-100 text-emerald-700',
                    content: '這些 Cookie 對平台正常運作不可或缺，包括保持登入狀態、記住購物車內容、處理安全支付等。無法關閉。',
                  },
                  {
                    title: '功能 Cookie',
                    badge: '可選',
                    badgeColor: 'bg-sky-100 text-sky-700',
                    content: '用於記住你的偏好設定，如語言、搜尋歷史、篩選條件等，使你的下次瀏覽更便捷。關閉後部分個人化功能將不可用。',
                  },
                  {
                    title: '分析 Cookie',
                    badge: '可選',
                    badgeColor: 'bg-amber-100 text-amber-700',
                    content: '協助我們了解使用者如何使用平台（如造訪的頁面、停留時長），用於改進產品體驗。資料經過匿名化處理，不會識別個人身份。',
                  },
                  {
                    title: '行銷 Cookie',
                    badge: '可選',
                    badgeColor: 'bg-violet-100 text-violet-700',
                    content: '用於向你顯示與職業發展相關的廣告內容。我們不會將你的資料出售給廣告商。你可以隨時關閉此類 Cookie。',
                  },
                ].map((section) => (
                  <div key={section.title} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-bold text-[#0A1628]">{section.title}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${section.badgeColor}`}>
                        {section.badge}
                      </span>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{section.content}</p>
                  </div>
                ))}
                <div className="bg-violet-50 border border-violet-100 rounded-xl p-5">
                  <p className="text-violet-700 text-sm leading-relaxed">
                    如需調整 Cookie 偏好，請透過瀏覽器設定管理，或
                    <Link to="/contact?tab=student" className="font-semibold underline underline-offset-2 cursor-pointer mx-1">聯絡我們</Link>
                    獲取協助。
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LegalPage;
